import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import useTransparentLogo from '../hooks/useTransparentLogo';

export default function LoginPage() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState(null);
  const [cargando, setCargando] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const logoSrc  = useTransparentLogo();
  const { login }   = useAuth();
  const navigate    = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCargando(true);
    setError(null);
    try {
      const res = await api.post('/auth/login', { email: email.trim(), password: password.trim() });
      const { token, nombre, email: userEmail, rol } = res.data;
      login(token, { nombre, email: userEmail, rol });
      navigate('/siniestros', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Error al conectar con el servidor');
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-950">

      {/* ── Panel izquierdo: marca (solo desktop) ─────────────── */}
      <div className="hidden lg:flex lg:w-1/2 flex-col items-center justify-between
                      bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900
                      relative overflow-hidden px-12 py-12">

        {/* Círculos decorativos */}
        <div className="absolute top-[-80px] left-[-80px] w-64 h-64 rounded-full
                        bg-blue-700 opacity-10 blur-3xl" />
        <div className="absolute bottom-[-60px] right-[-60px] w-80 h-80 rounded-full
                        bg-blue-500 opacity-10 blur-3xl" />
        <div className="absolute top-1/2 right-[-40px] w-48 h-48 rounded-full
                        bg-indigo-600 opacity-10 blur-2xl" />

        {/* Logo centrado — ocupa el espacio disponible entre los círculos */}
        <div className="relative z-10 flex flex-col items-center justify-center flex-1 w-full">
          <img
            src={logoSrc}
            alt="Mondepin"
            className="w-80 h-auto rounded-3xl animate-[float_3s_ease-in-out_infinite]"
            style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.9)) drop-shadow(0 0 12px rgba(255,255,255,0.6)) drop-shadow(-1px 0 0 rgba(0,0,0,0.8)) drop-shadow(1px 0 0 rgba(0,0,0,0.8)) drop-shadow(0 -1px 0 rgba(0,0,0,0.8)) drop-shadow(0 1px 0 rgba(0,0,0,0.8)) drop-shadow(0 8px 28px rgba(0,0,0,0.5))', imageRendering: 'auto' }}
          />
          <p className="text-blue-300 text-lg font-medium mt-6">
            Gestión de Siniestros
          </p>
        </div>

        {/* Estadísticas — ancladas al pie del panel */}
        <div className="relative z-10 w-full pb-8">
          <div className="grid grid-cols-2 gap-3 text-left">
            {[
              { label: 'Siniestros',    value: '+36.000' },
              { label: 'Partes',        value: '+56.000' },
              { label: 'Importes',      value: '+44.000' },
              { label: 'Años de datos', value: '20+' },
            ].map(item => (
              <div key={item.label}
                   className="bg-blue-900 bg-opacity-40 rounded-xl px-4 py-3
                              ring-1 ring-blue-700 ring-opacity-30">
                <p className="text-2xl font-bold text-white">{item.value}</p>
                <p className="text-blue-400 text-xs mt-0.5">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Panel derecho: formulario ──────────────────────────── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12
                      bg-slate-950">

        {/* Logo visible solo en móvil */}
        <div className="lg:hidden flex flex-col items-center mb-8">
          <img
            src={logoSrc}
            alt="Mondepin"
            className="w-56 h-auto rounded-2xl mb-3"
            style={{ filter: 'drop-shadow(0 0 5px rgba(255,255,255,0.9)) drop-shadow(0 0 10px rgba(255,255,255,0.6)) drop-shadow(-1px 0 0 rgba(0,0,0,0.8)) drop-shadow(1px 0 0 rgba(0,0,0,0.8)) drop-shadow(0 -1px 0 rgba(0,0,0,0.8)) drop-shadow(0 1px 0 rgba(0,0,0,0.8))', imageRendering: 'auto' }}
          />
          <p className="text-blue-400 text-sm">Gestión de Siniestros</p>
        </div>

        {/* Tarjeta del formulario */}
        <div className="w-full max-w-md
                        bg-slate-900 rounded-2xl shadow-2xl
                        ring-1 ring-slate-700
                        p-8
                        animate-[slideUp_0.4s_ease-out]">

          <div className="mb-7">
            <h2 className="text-2xl font-bold text-white">Iniciar sesión</h2>
            <p className="text-slate-400 text-sm mt-1">Introduce tus credenciales para acceder</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Email
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </span>
                <input
                  type="text"
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="admin@mondepin.com"
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl
                             pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                             transition"
                />
              </div>
            </div>

            {/* Contraseña */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Contraseña
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </span>
                <input
                  type={showPass ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl
                             pl-10 pr-10 py-2.5 text-sm text-white placeholder-slate-500
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                             transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500
                             hover:text-slate-300 transition"
                  tabIndex={-1}
                >
                  {showPass ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 bg-red-950 border border-red-800
                              text-red-400 text-sm rounded-xl px-4 py-3">
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            )}

            {/* Botón */}
            <button
              type="submit"
              disabled={cargando}
              className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-blue-900
                         text-white font-semibold py-2.5 rounded-xl
                         transition-all duration-200
                         flex items-center justify-center gap-2
                         shadow-lg shadow-blue-900/40 mt-2"
            >
              {cargando ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10"
                            stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Entrando...
                </>
              ) : (
                'Iniciar sesión'
              )}
            </button>
          </form>
        </div>

        <p className="mt-6 text-slate-600 text-xs">
          Mondepin © {new Date().getFullYear()}
        </p>
      </div>

      {/* Animaciones CSS */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-10px); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
